import React, { useState, useEffect } from 'react';
import { X, Search, Check } from 'lucide-react';
import { Class, getClasses } from '../../utils/subjectsClasses';

interface ClassModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (classes: Class[]) => void;
    selectedClasses: Class[];
    title?: string;
}

const ClassModal: React.FC<ClassModalProps> = ({
    isOpen,
    onClose,
    onSelect,
    selectedClasses,
    title = "Select Classes"
}) => {
    const [classes, setClasses] = useState<Class[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [tempSelected, setTempSelected] = useState<Class[]>(selectedClasses);

    useEffect(() => {
        if (isOpen) {
            fetchClasses();
            setTempSelected(selectedClasses);
        }
    }, [isOpen, selectedClasses]);

    const fetchClasses = async () => {
        setLoading(true);
        try {
            const classesData = await getClasses();
            setClasses(classesData);
        } catch (error) {
            console.error('Error fetching classes:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredClasses = classes.filter(cls =>
        cls.grade.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cls.section.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleClassToggle = (cls: Class) => {
        setTempSelected(prev => {
            const isSelected = prev.some(c => c.id === cls.id);
            if (isSelected) {
                return prev.filter(c => c.id !== cls.id);
            } else {
                return [...prev, cls];
            }
        });
    };

    const handleConfirm = () => {
        onSelect(tempSelected);
        onClose();
    };

    const handleCancel = () => {
        setTempSelected(selectedClasses);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
                    <button
                        onClick={handleCancel}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Search */}
                <div className="p-6 border-b border-gray-200">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <input
                            type="text"
                            placeholder="Search classes..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                    </div>
                </div>

                {/* Classes List */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="flex items-center justify-center h-32">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {filteredClasses.length === 0 ? (
                                <div className="text-center py-8">
                                    <p className="text-gray-500">No classes found</p>
                                </div>
                            ) : (
                                filteredClasses.map((cls) => {
                                    const isSelected = tempSelected.some(c => c.id === cls.id);
                                    return (
                                        <div
                                            key={cls.id}
                                            onClick={() => handleClassToggle(cls)}
                                            className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${isSelected
                                                    ? 'border-emerald-500 bg-emerald-50'
                                                    : 'border-gray-200 hover:border-emerald-300 hover:bg-gray-50'
                                                }`}
                                        >
                                            <div className="flex-1">
                                                <div className="flex items-center space-x-3">
                                                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected
                                                            ? 'border-emerald-500 bg-emerald-500'
                                                            : 'border-gray-300'
                                                        }`}>
                                                        {isSelected && <Check className="h-3 w-3 text-white" />}
                                                    </div>
                                                    <div>
                                                        <h3 className="font-medium text-gray-900">
                                                            {cls.grade} - {cls.section}
                                                        </h3>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-6 border-t border-gray-200">
                    <div className="text-sm text-gray-500">
                        {tempSelected.length} class{tempSelected.length !== 1 ? 'es' : ''} selected
                    </div>
                    <div className="flex space-x-3">
                        <button
                            onClick={handleCancel}
                            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleConfirm}
                            className="px-4 py-2 bg-emerald-600 text-white hover:bg-emerald-700 rounded-lg transition-colors"
                        >
                            Confirm Selection
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClassModal;
